import { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock, Play, Search, Square, Trash2 } from 'lucide-react';
import type { TicketSummary, TimerTicketOption } from '@t3/shared';
import { apiFetch } from '../api/client.ts';
import {
  ALL_BUCKETS,
  TICKETLESS_BILLABLE_BUCKETS,
  bucketForToggle,
  bucketLabel,
  derivedBucketForTicketType,
  isBillableBucket,
  type BillingBucket,
} from './buckets.ts';
import { elapsedMinutes, formatDuration, formatElapsed, useTimer } from './useTimer.ts';
import {
  MAX_TIME_ENTRY_MINUTES,
  REASON_REQUIRED_MESSAGE,
  RUNAWAY_TIMER_MINUTES,
  nonBillableReasonRequired,
} from '@t3/shared';

interface OrgOption {
  id: string;
  name: string;
}

type Tab = 'recent' | 'mine' | 'search' | 'none';

const LAST_ORG_KEY = 't3.timer.lastOrganizationId';

const TABS: { key: Tab; label: string }[] = [
  { key: 'recent', label: 'Recent' },
  { key: 'mine', label: 'Mine' },
  { key: 'search', label: 'Search' },
  { key: 'none', label: 'No ticket' },
];

export function TimerPanel({ compact = false }: { compact?: boolean }): React.ReactElement {
  const t = useTimer();
  return (
    <div
      className={
        'flex flex-col gap-3 bg-white text-[var(--color-body)] ' + (compact ? 'p-3' : 'p-4')
      }
    >
      {t.timer ? <RunningView t={t} /> : <IdleView t={t} />}
    </div>
  );
}

// ---- running -----------------------------------------------------------------

function RunningView({ t }: { t: ReturnType<typeof useTimer> }): React.ReactElement {
  const timer = t.timer!;
  const [showAll, setShowAll] = useState(false);

  // What the entry will actually bill as. A null billingBucket means the
  // server will derive it from the ticket type at stop, so preview that.
  const effective: BillingBucket | null =
    timer.billingBucket ?? derivedBucketForTicketType(timer.ticketType);
  const billable = effective ? isBillableBucket(effective) : true;

  // Computed here as well as reported by the server so the panel asks for a
  // real duration up front, instead of only after Stop comes back refused.
  const elapsed = elapsedMinutes(timer.startedAt);
  const runaway = t.runaway || elapsed > RUNAWAY_TIMER_MINUTES;

  async function setSide(nextBillable: boolean) {
    const next = bucketForToggle({
      billable: nextBillable,
      derived: effective,
      hasTicket: !!timer.ticketId,
    });
    await t.update({ billingBucket: next });
  }

  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-3xl font-bold tabular-nums text-[var(--color-header)]">
            {formatElapsed(timer.startedAt)}
          </div>
          <div className="mt-0.5 truncate text-xs text-[var(--color-muted)]">
            {timer.ticketId
              ? `T-${timer.ticketDisplayNumber} · ${timer.organizationName ?? ''}`
              : (timer.organizationName ?? 'Internal')}
          </div>
          <div className="truncate text-xs text-[var(--color-muted)]">
            {timer.ticketId ? timer.ticketSubject : effective ? bucketLabel(effective) : ''}
          </div>
        </div>
        <Clock className="h-5 w-5 shrink-0 text-[var(--color-t3-blue)]" />
      </div>

      <BillableToggle
        billable={billable}
        resolved={effective}
        disabled={t.busy}
        onChange={(b) => void setSide(b)}
        showAll={showAll}
        onToggleShowAll={() => setShowAll((s) => !s)}
        options={ALL_BUCKETS.filter((b) => (timer.ticketId ? b.inTicketed : b.inTicketless))}
        onPickExact={(b) => void t.update({ billingBucket: b })}
        value={timer.billingBucket}
      />

      <RunningReason t={t} effective={effective} />

      {t.error && !runaway && <p className="text-xs text-[var(--color-danger)]">{t.error}</p>}

      {runaway ? (
        <RunawayResolve t={t} elapsed={elapsed} />
      ) : (
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void t.stop()}
          disabled={t.busy}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-[var(--radius-button)] bg-[var(--color-t3-blue)] px-4 py-2 text-sm font-semibold text-white transition hover:brightness-95 disabled:bg-[#cccccc] disabled:text-[#888888]"
        >
          <Square className="h-3.5 w-3.5 fill-current" />
          {t.busy ? 'Working…' : 'Stop & log'}
        </button>
        <button
          type="button"
          title="Discard without logging time"
          onClick={() => {
            if (confirm('Discard this timer without logging time?')) void t.cancel();
          }}
          disabled={t.busy}
          className="inline-flex items-center justify-center rounded-[var(--radius-button)] border border-[var(--color-border)] px-3 py-2 text-[var(--color-muted)] transition hover:border-[var(--color-danger)] hover:text-[var(--color-danger)]"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      )}
    </>
  );
}

// A timer that ran past RUNAWAY_TIMER_MINUTES is a clock someone forgot, not a
// work session, so it will not commit its elapsed time. The tech says how long
// the work actually took, or throws it away. Anything over 24h could never be
// saved at all — `time_entries.duration_minutes` caps there — which is what
// used to leave a forgotten timer stuck forever.
function RunawayResolve({
  t,
  elapsed,
}: {
  t: ReturnType<typeof useTimer>;
  elapsed: number;
}): React.ReactElement {
  const [hours, setHours] = useState('');
  const parsed = Number(hours);
  const minutes = Number.isFinite(parsed) ? Math.round(parsed * 60) : NaN;
  const valid = Number.isFinite(minutes) && minutes >= 1 && minutes <= MAX_TIME_ENTRY_MINUTES;

  return (
    <div className="space-y-2 rounded-[var(--radius-card)] border border-amber-300 bg-amber-50 p-3">
      <p className="text-xs font-semibold text-amber-900">
        This timer has been running {formatDuration(elapsed)}.
      </p>
      <p className="text-xs text-amber-800">
        That's too long to log as-is. Enter how many hours you actually worked, or discard it.
      </p>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="0.25"
          max={MAX_TIME_ENTRY_MINUTES / 60}
          step="0.25"
          value={hours}
          autoFocus
          onChange={(e) => setHours(e.target.value)}
          placeholder="e.g. 1.5"
          aria-label="Hours worked"
          className="w-24 rounded-[var(--radius-button)] border border-[var(--color-border)] px-2 py-1.5 text-sm"
        />
        <span className="text-xs text-amber-800">hours</span>
        <button
          type="button"
          onClick={() => void t.stop(minutes)}
          disabled={t.busy || !valid}
          className="ml-auto inline-flex items-center justify-center gap-2 rounded-[var(--radius-button)] bg-[var(--color-t3-blue)] px-3 py-1.5 text-sm font-semibold text-white transition hover:brightness-95 disabled:bg-[#cccccc] disabled:text-[#888888]"
        >
          <Square className="h-3.5 w-3.5 fill-current" />
          {t.busy ? 'Working…' : 'Log it'}
        </button>
        <button
          type="button"
          title="Discard without logging time"
          onClick={() => {
            if (confirm('Discard this timer without logging any time?')) void t.cancel();
          }}
          disabled={t.busy}
          className="inline-flex items-center justify-center rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2.5 py-1.5 text-[var(--color-muted)] transition hover:border-[var(--color-danger)] hover:text-[var(--color-danger)]"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      {t.error && <p className="text-xs text-[var(--color-danger)]">{t.error}</p>}
    </div>
  );
}

// ---- idle --------------------------------------------------------------------

function IdleView({ t }: { t: ReturnType<typeof useTimer> }): React.ReactElement {
  const [orgs, setOrgs] = useState<OrgOption[] | null>(null);
  const [organizationId, setOrganizationId] = useState<string>(
    () => localStorage.getItem(LAST_ORG_KEY) ?? '',
  );
  const [tab, setTab] = useState<Tab>('recent');
  const [options, setOptions] = useState<{ recent: TimerTicketOption[]; assigned: TimerTicketOption[] } | null>(null);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<TicketSummary[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<TimerTicketOption | null>(null);
  const [ticketlessBucket, setTicketlessBucket] = useState<BillingBucket>('non_billable_internal');
  const [billable, setBillable] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [exactBucket, setExactBucket] = useState<BillingBucket | null>(null);
  const [reason, setReason] = useState('');

  const hasTicket = tab !== 'none' && !!picked;

  useEffect(() => {
    apiFetch<{ organizations: OrgOption[] }>('/api/organizations')
      .then((r) => setOrgs(r.organizations.map((o) => ({ id: o.id, name: o.name }))))
      .catch(() => setOrgs([]));
  }, []);

  // Recent + assigned, narrowed by the customer selector.
  useEffect(() => {
    setOptions(null);
    const qs = organizationId ? `?organizationId=${organizationId}` : '';
    apiFetch<{ recent: TimerTicketOption[]; assigned: TimerTicketOption[] }>(`/api/timer/options${qs}`)
      .then(setOptions)
      .catch(() => setOptions({ recent: [], assigned: [] }));
  }, [organizationId]);

  // Search tab. /api/tickets already filters by q + org + closed together and
  // returns the org id, which /api/search doesn't.
  useEffect(() => {
    if (tab !== 'search' || !q.trim()) {
      setHits(null);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const id = setTimeout(() => {
      const p = new URLSearchParams({ q: q.trim(), closed: 'false', limit: '25' });
      if (organizationId) p.set('organizationId', organizationId);
      apiFetch<{ tickets: TicketSummary[] }>(`/api/tickets?${p.toString()}`)
        .then((r) => {
          if (!cancelled) setHits(r.tickets);
        })
        .catch(() => {
          if (!cancelled) setHits([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [tab, q, organizationId]);

  const chooseOrg = useCallback((id: string) => {
    setOrganizationId(id);
    if (id) localStorage.setItem(LAST_ORG_KEY, id);
    else localStorage.removeItem(LAST_ORG_KEY);
    // The picked ticket may not belong to the new customer.
    setPicked(null);
  }, []);

  // Picking a ticket back-fills the customer so the two can never disagree.
  const chooseTicket = useCallback((opt: TimerTicketOption) => {
    setPicked(opt);
    setOrganizationId(opt.organizationId);
    localStorage.setItem(LAST_ORG_KEY, opt.organizationId);
    setExactBucket(null);
    setShowAll(false);
    const derived = derivedBucketForTicketType(opt.type);
    // Open the toggle on whichever side this ticket already bills as.
    setBillable(derived ? isBillableBucket(derived) : true);
  }, []);

  const derived = useMemo(
    () => (hasTicket && picked ? derivedBucketForTicketType(picked.type) : null),
    [hasTicket, picked],
  );

  const resolvedBucket: BillingBucket = useMemo(() => {
    if (exactBucket) return exactBucket;
    if (!hasTicket) return billable ? bucketForToggle({ billable, derived: null, hasTicket: false }) : ticketlessBucket;
    return bucketForToggle({ billable, derived, hasTicket: true });
  }, [exactBucket, hasTicket, billable, derived, ticketlessBucket]);

  const needsOrg = !hasTicket && TICKETLESS_BILLABLE_BUCKETS.has(resolvedBucket);
  // On a ticket the reason is only asked for once a ticket is picked; the
  // bucket before that is a placeholder.
  const needsReason = (tab === 'none' || hasTicket) && nonBillableReasonRequired(resolvedBucket);
  const canStart =
    (!needsReason || !!reason.trim()) &&
    (hasTicket || (!!resolvedBucket && (!needsOrg || !!organizationId)));

  async function start() {
    if (hasTicket && picked) {
      // Only send a bucket when the tech overrode the derivation — otherwise
      // let the server derive it (it knows about project billing mode).
      const isOverride = exactBucket !== null || (derived !== null && resolvedBucket !== derived);
      await t.start({
        ticketId: picked.id,
        ...(isOverride ? { billingBucket: resolvedBucket } : {}),
        ...(needsReason ? { notes: reason.trim() } : {}),
      });
      return;
    }
    await t.start({
      billingBucket: resolvedBucket,
      ...(organizationId ? { organizationId } : {}),
      ...(reason.trim() ? { notes: reason.trim() } : {}),
    });
  }

  const list: TimerTicketOption[] =
    tab === 'recent'
      ? (options?.recent ?? [])
      : tab === 'mine'
        ? (options?.assigned ?? [])
        : tab === 'search'
          ? (hits ?? []).map((h) => ({
              id: h.id,
              displayNumber: h.displayNumber,
              subject: h.subject,
              organizationId: h.organizationId,
              organizationName: h.organizationName,
              status: h.status,
              type: h.type,
            }))
          : [];

  return (
    <>
      <label className="block">
        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[var(--color-muted)]">
          Customer
        </span>
        <select
          value={organizationId}
          onChange={(e) => chooseOrg(e.target.value)}
          className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2.5 py-1.5 text-sm outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
        >
          <option value="">All customers</option>
          {(orgs ?? []).map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </label>

      <div>
        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[var(--color-muted)]">
          Ticket
        </span>
        <div className="flex overflow-hidden rounded-[var(--radius-button)] border border-[var(--color-border)] text-xs">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              type="button"
              onClick={() => {
                setTab(tb.key);
                if (tb.key === 'none') {
                  setPicked(null);
                  setExactBucket(null);
                  setShowAll(false);
                  setBillable(false);
                }
              }}
              className={
                'flex-1 px-2 py-1.5 transition ' +
                (tab === tb.key
                  ? 'bg-[var(--color-t3-blue)] font-semibold text-white'
                  : 'bg-white text-[var(--color-muted)] hover:bg-[var(--color-surface)]')
              }
            >
              {tb.label}
            </button>
          ))}
        </div>

        {tab === 'search' && (
          <div className="mt-2 flex items-center gap-2 rounded-[var(--radius-button)] border border-[var(--color-border)] px-2 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-[var(--color-muted)]" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Subject or T-number…"
              className="w-full bg-transparent text-sm outline-none"
            />
          </div>
        )}

        {tab === 'none' ? (
          <div className="mt-2">
            <select
              value={ticketlessBucket}
              onChange={(e) => {
                setTicketlessBucket(e.target.value as BillingBucket);
                setExactBucket(e.target.value as BillingBucket);
                setBillable(isBillableBucket(e.target.value as BillingBucket));
              }}
              className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2.5 py-1.5 text-sm outline-none focus:border-[var(--color-t3-blue)]"
            >
              {ALL_BUCKETS.filter((b) => b.inTicketless).map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
            {needsOrg && !organizationId && (
              <p className="mt-1 text-xs text-[var(--color-danger)]">
                Pick the customer this time bills to.
              </p>
            )}
            {needsReason && (
              <ReasonInput value={reason} onChange={setReason} />
            )}
          </div>
        ) : (
          <ul className="mt-2 max-h-40 overflow-y-auto rounded-[var(--radius-button)] border border-[var(--color-border)]">
            {tab === 'search' && !q.trim() ? (
              <li className="px-2.5 py-3 text-xs text-[var(--color-muted)]">Type to search.</li>
            ) : searching && !hits ? (
              <li className="px-2.5 py-3 text-xs text-[var(--color-muted)]">Searching…</li>
            ) : list.length === 0 ? (
              <li className="px-2.5 py-3 text-xs text-[var(--color-muted)]">
                {tab === 'recent' ? 'No recently timed tickets.' : 'Nothing here.'}
              </li>
            ) : (
              list.map((opt) => (
                <li key={opt.id}>
                  <button
                    type="button"
                    onClick={() => chooseTicket(opt)}
                    className={
                      'block w-full px-2.5 py-1.5 text-left text-xs transition ' +
                      (picked?.id === opt.id
                        ? 'bg-[var(--color-t3-blue)]/10'
                        : 'hover:bg-[var(--color-surface)]')
                    }
                  >
                    <span className="font-mono text-[var(--color-muted)]">T-{opt.displayNumber}</span>{' '}
                    <span className="text-[var(--color-body)]">{opt.subject}</span>
                    <span className="block truncate text-[10px] text-[var(--color-muted)]">
                      {opt.organizationName}
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>

      {tab !== 'none' && (
        <BillableToggle
          billable={billable}
          resolved={hasTicket ? resolvedBucket : null}
          disabled={!hasTicket}
          onChange={(b) => {
            setBillable(b);
            setExactBucket(null);
          }}
          showAll={showAll}
          onToggleShowAll={() => setShowAll((s) => !s)}
          options={ALL_BUCKETS.filter((b) => b.inTicketed)}
          onPickExact={(b) => {
            setExactBucket(b);
            setBillable(isBillableBucket(b));
          }}
          value={exactBucket}
        />
      )}

      {tab !== 'none' && needsReason && <ReasonInput value={reason} onChange={setReason} />}

      {t.error && <p className="text-xs text-[var(--color-danger)]">{t.error}</p>}

      <button
        type="button"
        onClick={() => void start()}
        disabled={!canStart || t.busy}
        className="inline-flex items-center justify-center gap-2 rounded-[var(--radius-button)] bg-[var(--color-t3-blue)] px-4 py-2 text-sm font-semibold text-white transition hover:brightness-95 disabled:bg-[#cccccc] disabled:text-[#888888]"
      >
        <Play className="h-3.5 w-3.5 fill-current" />
        {t.busy ? 'Starting…' : 'Start'}
      </button>
    </>
  );
}

// ---- shared bits -------------------------------------------------------------

function ReasonInput({
  value,
  onChange,
  onBlur,
  disabled,
  required = true,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
}): React.ReactElement {
  return (
    <label className="mt-2 block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[var(--color-muted)]">
        {required ? 'Reason (required)' : 'Notes'}
      </span>
      <input
        type="text"
        value={value}
        maxLength={2000}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder="What is this time for?"
        className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2.5 py-1.5 text-sm outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
      />
    </label>
  );
}

// The running timer's notes, which become the entry's notes. Required while it
// bills as non-billable, ticket or not; shown on billable time too so a reason
// can be given before switching to non-billable. Saved on blur; a blank never
// overwrites a required reason (the server would refuse it anyway).
function RunningReason({
  t,
  effective,
}: {
  t: ReturnType<typeof useTimer>;
  effective: BillingBucket | null;
}): React.ReactElement {
  const timer = t.timer!;
  const [value, setValue] = useState(timer.notes ?? '');
  useEffect(() => setValue(timer.notes ?? ''), [timer.notes]);
  const required = nonBillableReasonRequired(effective);

  function save() {
    const next = value.trim();
    if (next === (timer.notes ?? '').trim()) return;
    if (!next && required) {
      setValue(timer.notes ?? '');
      return;
    }
    void t.update({ notes: next || null });
  }

  return (
    <div>
      <ReasonInput
        value={value}
        onChange={setValue}
        onBlur={save}
        disabled={t.busy}
        required={required}
      />
      {required && !value.trim() && (
        <p className="mt-1 text-xs text-[var(--color-danger)]">{REASON_REQUIRED_MESSAGE}</p>
      )}
    </div>
  );
}

function BillableToggle({
  billable,
  resolved,
  disabled,
  onChange,
  showAll,
  onToggleShowAll,
  options,
  onPickExact,
  value,
}: {
  billable: boolean;
  resolved: BillingBucket | null;
  disabled?: boolean;
  onChange: (billable: boolean) => void;
  showAll: boolean;
  onToggleShowAll: () => void;
  options: { value: BillingBucket; label: string }[];
  onPickExact: (b: BillingBucket) => void;
  value: BillingBucket | null;
}): React.ReactElement {
  return (
    <div>
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[var(--color-muted)]">
        Time type
      </span>
      <div className="flex overflow-hidden rounded-[var(--radius-button)] border border-[var(--color-border)] text-xs">
        {[
          { key: true, label: 'Billable' },
          { key: false, label: 'Non-billable' },
        ].map((side) => (
          <button
            key={String(side.key)}
            type="button"
            disabled={disabled}
            onClick={() => onChange(side.key)}
            className={
              'flex-1 px-2 py-1.5 transition disabled:opacity-50 ' +
              (billable === side.key
                ? 'bg-[var(--color-t3-blue)] font-semibold text-white'
                : 'bg-white text-[var(--color-muted)] hover:bg-[var(--color-surface)]')
            }
          >
            {side.label}
          </button>
        ))}
      </div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="truncate text-[11px] text-[var(--color-muted)]">
          {resolved ? bucketLabel(resolved) : 'Pick a ticket'}
        </span>
        <button
          type="button"
          onClick={onToggleShowAll}
          className="shrink-0 text-[11px] text-[var(--color-t3-blue)] hover:underline"
        >
          {showAll ? 'less' : 'other…'}
        </button>
      </div>
      {showAll && (
        <select
          value={value ?? resolved ?? ''}
          onChange={(e) => onPickExact(e.target.value as BillingBucket)}
          className="mt-1 block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2.5 py-1.5 text-xs outline-none focus:border-[var(--color-t3-blue)]"
        >
          <option value="">— pick one —</option>
          {options.map((b) => (
            <option key={b.value} value={b.value}>
              {b.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
